import {
	ChangeDetectionStrategy,
	Component,
	ViewChild,
	OnInit,
	OnDestroy
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { CommentFilterService, UniqueUser } from '@go-modules/services/comment-filter/comment-filter.service';
import { CommentListService } from '@go-modules/services/comment-list/comment-list.service';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'comment-filter-dropdown',
	templateUrl: './comment-filter-dropdown.component.html',
	styleUrls: ['./comment-filter-dropdown.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class CommentFilterDropdownComponent implements OnInit, OnDestroy {
	public uniqueUsers$ = new BehaviorSubject<UniqueUser[]>([]);
	public form = new UntypedFormGroup({
		selectedUsers: new UntypedFormControl([0])
	});
	@ViewChild('allSelected') private allSelected: MatOption;
	private destroy$ = new Subject<void>();

	constructor (
		public commentFilterService: CommentFilterService,
		public commentListService: CommentListService
	) {}

	public ngOnInit () {
		this.commentListService.comments$
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				const comments = this.commentListService.currentComments;
				const previousUniqueUsers = [...this.uniqueUsers$.value];
				if (!previousUniqueUsers.length) {
					this.uniqueUsers$.next(this.commentFilterService.getUniqueUsers(comments));
					// select all when unique users are first populated
					this.form.controls.selectedUsers.patchValue([
						...this.uniqueUsers$.value.map((user) => user.id), 0
					]);
					this.commentFilterService.selectedUserIds = [];
					return;
				}

				this.uniqueUsers$.next(this.commentFilterService.getUniqueUsers(comments));

				// as well as automatically select new users when new comments are added
				if (this.uniqueUsers$.value.length > previousUniqueUsers.length) {
					const newUsers = this.uniqueUsers$.value.filter((user) => {
						return !previousUniqueUsers.some((prevUser) => prevUser.id === user.id);
					});
					this.form.controls.selectedUsers.patchValue([
						...this.form.controls.selectedUsers.value,
						...newUsers.map((user) => user.id)
					]);
					if (!this.allSelected.selected) {
						this.commentFilterService.selectedUserIds = this.form.controls.selectedUsers.value;
					}
				// remove users from selected users when unique users are removed
				} else if (this.uniqueUsers$.value.length < previousUniqueUsers.length) {
					const removedUsers = previousUniqueUsers.filter((user) => {
						return !this.uniqueUsers$.value.some((currentUser) => currentUser.id === user.id);
					});
					this.form.controls.selectedUsers.patchValue(
						this.form.controls.selectedUsers.value.filter((id) => {
							return !removedUsers.some((user) => user.id === id);
						})
					);
					if (this.allSelected.selected) {
						this.commentFilterService.selectedUserIds = [];
					} else if (this.form.controls.selectedUsers.value.length === 0) {
						this.commentFilterService.selectedUserIds = null;
					} else {
						this.commentFilterService.selectedUserIds = this.form.controls.selectedUsers.value;
					}
				}
				if (previousUniqueUsers.length !== this.uniqueUsers$.value.length) {
					this.commentListService.filterChanged();
				}
			});
	}

	public ngOnDestroy () {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public toggleOne () {
		if (this.allSelected.selected) {
			this.allSelected.deselect();
			this.commentFilterService.selectedUserIds = this.form.controls.selectedUsers.value;
			// if only one user exists and it is deselected
			if (this.form.controls.selectedUsers.value.length === 0) {
				this.commentFilterService.selectedUserIds = null;
			}
		} else {
			if (this.form.controls.selectedUsers.value.length === this.uniqueUsers$.value.length) {
				this.allSelected.select();
				this.form.controls.selectedUsers
					.patchValue([...this.uniqueUsers$.value.map((user) => user.id), 0]);
				this.commentFilterService.selectedUserIds = [];
			} else if (this.form.controls.selectedUsers.value.length === 0) {
				this.form.controls.selectedUsers.patchValue([]);
				this.commentFilterService.selectedUserIds = null;
			} else {
				this.commentFilterService.selectedUserIds = this.form.controls.selectedUsers.value;
			}
		}
		this.commentListService.filterChanged();
	}

	public toggleAll () {
		if (this.allSelected.selected) {
			this.form.controls.selectedUsers
				.patchValue([...this.uniqueUsers$.value.map((user) => user.id), 0]);
			this.commentFilterService.selectedUserIds = [];
		} else {
			this.form.controls.selectedUsers.patchValue([]);
			this.commentFilterService.selectedUserIds = null;
		}
		this.commentListService.filterChanged();
	}
}
